<template>
  <div class="mc-portal">
    <PortalTopBar :role="barRole" />

    <div class="pt-container">
      <div class="pt-head">
        <div>
          <div class="pt-kicker">Rollouts</div>
          <h1 class="pt-title">Manage city rollouts</h1>
          <p class="pt-sub">Move each city from contract to live. The city's contact gets an email at every step, with your note if you add one.</p>
        </div>
      </div>

      <!-- Stage board -->
      <div class="board" role="tablist" aria-label="Filter by stage">
        <button type="button" class="col" :class="{ on: stageFilter === '' }" @click="stageFilter = ''">
          <span class="col-n">{{ cities.length }}</span><span class="col-name">All cities</span>
        </button>
        <button v-for="s in STAGES" :key="s.name" type="button" class="col" :class="{ on: stageFilter === s.name, live: s.name === 'Live' }" @click="stageFilter = s.name">
          <span class="col-n">{{ countAt(s.name) }}</span><span class="col-name">{{ s.name }}</span>
        </button>
      </div>

      <div class="layout">
        <!-- City list -->
        <section class="pt-card list-card">
          <label class="pt-search">
            <SiteIcon name="search" :size="16" />
            <input v-model="search" type="text" placeholder="Search city, state or contact email" aria-label="Search cities" />
          </label>

          <div v-if="loading" class="list-pad"><div v-for="n in 4" :key="n" class="pt-skeleton"></div></div>
          <div v-else-if="!filtered.length" class="pt-empty">
            <SiteIcon name="city" :size="36" />
            <p>{{ cities.length ? "No cities match." : "No cities yet. Onboard one to start its rollout." }}</p>
          </div>
          <div v-else class="list">
            <button v-for="c in filtered" :key="c.id" type="button" class="row" :class="{ on: c.id === selectedId }" @click="select(c.id)">
              <span class="row-main">
                <b>{{ cityName(c) }}<span v-if="c.state" class="pt-muted">, {{ c.state }}</span></b>
                <span class="pt-hint">{{ c.ownerEmail || "No contact email" }}</span>
              </span>
              <span class="row-side">
                <span class="pt-badge" :class="stageTone(c.appProgress)">{{ normalizeStage(c.appProgress) }}</span>
                <span class="pt-hint">{{ daysAt(c) }}</span>
              </span>
            </button>
          </div>
        </section>

        <!-- Selected city -->
        <section class="pt-card detail-card">
          <div v-if="!selected" class="pt-empty">
            <SiteIcon name="flag" :size="40" />
            <h3>Pick a city</h3>
            <p>Its rollout and history show here.</p>
          </div>

          <template v-else>
            <div class="pt-card-head">
              <div>
                <h2 class="pt-card-title">{{ cityName(selected) }}</h2>
                <p class="pt-hint">{{ selected.fullName || "No contact name" }}<template v-if="selected.contactTitle">, {{ selected.contactTitle }}</template></p>
              </div>
              <router-link v-if="barRole === 'admin'" :to="`/admin/cities/${selected.id}`" class="pt-btn pt-btn-outline pt-btn-sm">Open city <SiteIcon name="arrowRight" :size="14" /></router-link>
            </div>

            <div class="timeline">
              <div v-for="(s, i) in STAGES" :key="s.name" class="tl" :class="{ done: i < selIdx || (i === selIdx && s.name === 'Live'), current: i === selIdx && s.name !== 'Live' }">
                <span class="dot"><SiteIcon v-if="i < selIdx || (i === selIdx && s.name === 'Live')" name="check" :size="14" /><span v-else>{{ i + 1 }}</span></span>
                <span class="tl-body">
                  <b>{{ s.name }}</b>
                  <span class="pt-hint">{{ s.blurb }}</span>
                </span>
                <span class="tl-date pt-hint">{{ stageDate(s.name) }}</span>
              </div>
            </div>

            <div class="move">
              <div class="pt-label">Move to</div>
              <div class="chips">
                <button
                  v-for="s in STAGES"
                  :key="s.name"
                  type="button"
                  class="pt-btn pt-btn-sm"
                  :class="newStage === s.name ? 'pt-btn-ink' : 'pt-btn-outline'"
                  :disabled="s.name === selStage"
                  @click="newStage = s.name"
                >
                  {{ s.name }}
                </button>
              </div>

              <label class="pt-field">
                <span class="pt-label">Note for the city <em>(optional)</em></span>
                <textarea v-model="note" class="pt-textarea" rows="2" maxlength="1000" placeholder="e.g. Please upload your FY27 real estate commitment by Friday."></textarea>
              </label>

              <div v-if="newStage" class="preview">
                <div class="pt-label">Email preview</div>
                <div class="mail">
                  <div class="mail-sub"><span class="pt-muted">To:</span> {{ selected.ownerEmail || "no email on file" }}</div>
                  <div class="mail-sub"><span class="pt-muted">Subject:</span> {{ cityName(selected) }} rollout: {{ newStage }}</div>
                  <p>{{ stageEmail(newStage) }}</p>
                  <p v-if="note.trim()" class="mail-note">{{ note.trim() }}<br /><span class="pt-muted">— {{ myName }}</span></p>
                </div>
              </div>

              <div class="go">
                <label class="pt-switch"><input v-model="notify" type="checkbox" /><span class="pt-switch-track"></span> Send the email</label>
                <button type="button" class="pt-btn pt-btn-primary" :disabled="!newStage || newStage === selStage || updating" @click="update">
                  <SiteIcon name="refresh" :size="16" /> {{ updating ? "Updating…" : `Move to ${newStage || "…"}` }}
                </button>
              </div>

              <div v-if="result" class="pt-notice" :class="result.tone">
                <SiteIcon :name="result.tone === 'info' ? 'check' : 'alert'" :size="17" /><span>{{ result.text }}</span>
              </div>
            </div>

            <div v-if="history.length" class="history">
              <div class="pt-label">History</div>
              <div v-for="(h, i) in history" :key="i" class="h">
                <span class="pt-hint">{{ formatDateTime(h.at) }}</span>
                <span><b>{{ h.from }}</b> → <b>{{ h.to }}</b> · {{ h.byName }}</span>
                <span v-if="h.note" class="h-note">“{{ h.note }}”</span>
              </div>
            </div>
          </template>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import "@/styles/portal.css";
import { auth, db, functions } from "@/firebase";
import { collection, onSnapshot, doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import PortalTopBar from "@/components/PortalTopBar.vue";
import SiteIcon from "@/components/SiteIcon.vue";
import { CITY_STAGES, normalizeStage, stageIndex, stageTone } from "@/config/cityPortal";
import { cityName, formatDate, formatDateTime, tsMs, toDate } from "@/config/format";

// Shown in the preview. The function sends the same wording (STAGE_EMAILS in index.ts).
const STAGE_EMAILS = {
  "Not Started": "Your rollout status is now Not Started.",
  Kickoff: "Your rollout has started. Your setup team will book a kickoff call with your office this week.",
  "Data Import": "We're ready for your files. Sign in to your portal to upload your bill (commitment) files, open parking tickets and staff list. Each file is checked as you upload it.",
  "Setup & Training": "Your departments, fees and receipts are being set up, and we'll schedule training for your staff.",
  Testing: "Everything is set up. We'll run test payments with your office before residents can pay.",
  Live: "You're live. Residents can now pay online, and your team can send change requests from the portal anytime.",
};

export default {
  name: "ManageAppProgressView",
  components: { PortalTopBar, SiteIcon },
  data() {
    return {
      STAGES: CITY_STAGES,
      cities: [],
      loading: true,
      search: "",
      stageFilter: "",
      selectedId: this.$route.query.city || null,
      newStage: "",
      note: "",
      notify: true,
      updating: false,
      result: null,
      barRole: "admin",
      myName: "",
    };
  },
  computed: {
    filtered() {
      const q = this.search.toLowerCase().trim();
      return this.cities
        .filter((c) => !this.stageFilter || normalizeStage(c.appProgress) === this.stageFilter)
        .filter((c) => !q || [cityName(c), c.state, c.ownerEmail, c.fullName].some((v) => (v || "").toLowerCase().includes(q)))
        .sort((a, b) => stageIndex(a.appProgress) - stageIndex(b.appProgress) || cityName(a).localeCompare(cityName(b)));
    },
    selected() {
      return this.cities.find((c) => c.id === this.selectedId) || null;
    },
    selStage() {
      return normalizeStage(this.selected?.appProgress);
    },
    selIdx() {
      return stageIndex(this.selected?.appProgress);
    },
    history() {
      return (this.selected?.stageHistory || []).slice().sort((a, b) => tsMs(b.at) - tsMs(a.at));
    },
  },
  mounted() {
    this._unsubAuth = auth.onAuthStateChanged(async (u) => {
      if (!u) {
        this.$router.push("/login");
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        const d = snap.exists() ? snap.data() : {};
        this.barRole = d.role === "Admin" ? "admin" : "staff";
        this.myName = `${d.firstName || ""} ${d.lastName || ""}`.trim() || d.fullName || u.displayName || u.email;
      } catch (e) {
        /* keep defaults */
      }
      this._unsub = onSnapshot(
        collection(db, "customers"),
        (snap) => {
          this.cities = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          this.loading = false;
          if (this.selectedId && !this.newStage) this.newStage = this.nextFor(this.selected);
        },
        (e) => {
          console.error("Error loading cities:", e);
          this.loading = false;
        }
      );
    });
  },
  beforeDestroy() {
    if (this._unsub) this._unsub();
    if (this._unsubAuth) this._unsubAuth();
  },
  methods: {
    cityName,
    formatDateTime,
    normalizeStage,
    stageTone,
    countAt(name) {
      return this.cities.filter((c) => normalizeStage(c.appProgress) === name).length;
    },
    nextFor(c) {
      if (!c) return "";
      const i = stageIndex(c.appProgress);
      return i >= CITY_STAGES.length - 1 ? "" : CITY_STAGES[i + 1].name;
    },
    select(id) {
      this.selectedId = id;
      this.note = "";
      this.result = null;
      this.newStage = this.nextFor(this.selected);
      if (this.$route.query.city !== id) this.$router.replace({ query: { ...this.$route.query, city: id } }).catch(() => {});
    },
    stageDate(name) {
      const dates = this.selected?.appProgressDates || {};
      const legacy = { Kickoff: "Designing", "Setup & Training": "Developing", Live: "Completed" };
      const v = dates[name] || dates[legacy[name]];
      return v ? formatDate(v, "") : "";
    },
    daysAt(c) {
      const stage = normalizeStage(c.appProgress);
      const d = toDate((c.appProgressDates || {})[stage]) || toDate(c.createdAt);
      if (!d) return "";
      const days = Math.floor((Date.now() - d.getTime()) / 86400000);
      return stage === "Live" ? `live ${days}d` : days === 0 ? "today" : `${days}d here`;
    },
    stageEmail(name) {
      return STAGE_EMAILS[name] || "";
    },
    async update() {
      const city = this.selected;
      const to = this.newStage;
      this.updating = true;
      this.result = null;
      try {
        const res = await httpsCallable(functions, "setCityStage")({ companyId: city.id, stage: to, note: this.note, notify: this.notify });
        const { emailSent, emailError } = res.data || {};
        if (!this.notify) this.result = { tone: "info", text: `${cityName(city)} moved to ${to}. No email was sent.` };
        else if (emailSent) this.result = { tone: "info", text: `${cityName(city)} moved to ${to}, and ${city.ownerEmail} was emailed.` };
        else this.result = { tone: "warn", text: `${cityName(city)} moved to ${to}, but the email didn't send: ${emailError || "unknown error"}. Check RESEND_API_KEY and MAIL_FROM.` };
        this.note = "";
        this.$nextTick(() => (this.newStage = this.nextFor(this.selected)));
      } catch (e) {
        console.error("setCityStage failed:", e);
        this.result = { tone: "error", text: "Couldn't update the stage: " + (e.message || "unknown error") };
      } finally {
        this.updating = false;
      }
    },
  },
};
</script>

<style scoped>
.board {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 22px;
}

.mc-portal .col {
  display: grid;
  gap: 2px;
  padding: 12px 14px;
  border: 1px solid var(--rule);
  border-top: 3px solid var(--rule-strong);
  border-radius: 4px 4px 8px 8px;
  background: var(--card);
  color: var(--ink);
  font-family: var(--sans);
  text-align: left;
  cursor: pointer;
  min-width: 0;
}

.mc-portal .col.live {
  border-top-color: var(--green);
}

.mc-portal .col.on {
  border-color: var(--ink);
  border-top-color: var(--ink);
  background: var(--ink);
  color: var(--on-navy);
}

.col-n {
  font-family: var(--serif);
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1;
}

.col-name {
  font-size: 12.5px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.layout {
  display: grid;
  grid-template-columns: 380px minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}

.layout .pt-card + .pt-card {
  margin-top: 0;
}

.list-card {
  padding: 14px;
  position: sticky;
  top: 86px;
}

.list,
.list-pad {
  display: grid;
  gap: 6px;
  margin-top: 12px;
  max-height: 62vh;
  overflow-y: auto;
}

.mc-portal .row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--rule);
  border-radius: 8px;
  background: var(--card);
  color: var(--ink);
  font-family: var(--sans);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.mc-portal .row:hover {
  border-color: var(--green);
}

.mc-portal .row.on {
  border-color: var(--green);
  box-shadow: inset 3px 0 0 var(--green);
  background: #f7faf6;
}

.row-main,
.row-side {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.row-main b {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-main .pt-hint {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-side {
  justify-items: end;
  flex-shrink: 0;
}

.timeline {
  display: grid;
  gap: 0;
  margin-bottom: 22px;
}

.tl {
  position: relative;
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  gap: 12px;
  padding-bottom: 16px;
}

.tl:not(:last-child)::after {
  content: "";
  position: absolute;
  left: 14px;
  top: 30px;
  bottom: 0;
  width: 2px;
  background: var(--rule);
}

.tl.done:not(:last-child)::after {
  background: var(--green);
}

.dot {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border: 2px solid var(--rule-strong);
  border-radius: 50%;
  background: var(--card);
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
}

.tl.done .dot {
  border-color: var(--green);
  background: var(--green);
  color: #fff;
}

.tl.current .dot {
  border-color: var(--brass);
  color: var(--brass);
  box-shadow: 0 0 0 4px rgba(168, 117, 47, 0.15);
}

.tl-body {
  display: grid;
  gap: 2px;
  padding-top: 4px;
  min-width: 0;
}

.tl:not(.done):not(.current) .tl-body b {
  color: var(--muted);
}

.tl-date {
  padding-top: 6px;
  white-space: nowrap;
}

.move {
  display: grid;
  gap: 14px;
  padding-top: 18px;
  border-top: 1px solid var(--rule);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: -6px;
}

.mail {
  padding: 14px 16px;
  border: 1px solid var(--rule);
  border-top: 3px solid var(--green);
  border-radius: 4px 4px 8px 8px;
  background: #fff;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-2);
}

.mail-sub {
  font-size: 13px;
}

.mail p {
  margin-top: 10px;
}

.mail-note {
  padding-left: 12px;
  border-left: 3px solid var(--green);
  color: var(--ink);
  white-space: pre-wrap;
}

.go {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.history {
  display: grid;
  gap: 8px;
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid var(--rule);
}

.h {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 13.5px;
  color: var(--ink-2);
}

.h-note {
  font-style: italic;
  color: var(--muted);
}

@media (max-width: 1000px) {
  .board {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .list-card {
    position: static;
  }
  .list {
    max-height: 340px;
  }
}

@media (max-width: 560px) {
  .board {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .go,
  .go .pt-btn {
    width: 100%;
  }
  .tl-date {
    display: none;
  }
}
</style>